// Shared colour math for the status-lifecycle colour pickers (master status
// pages + their admin/child status pages).

export const hexToRgb = (hex: string) => {
  const clean = (hex || "").replace("#", "").padStart(6, "0").slice(0, 6);
  const bigint = parseInt(clean, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
};

export const rgbToHex = (r: number, g: number, b: number) =>
  "#" +
  [r, g, b]
    .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0"))
    .join("");

// Bold/dark colour options offered when picking a master-status colour —
// deliberately darker shades (Material 700/800 range) so the status stays
// clearly visible as a chip, dot or icon.
export const MASTER_STATUS_COLOR_OPTIONS = [
  "#B71C1C", "#C2185B", "#7B1FA2", "#512DA8",
  "#303F9F", "#1976D2", "#0277BD", "#00796B",
  "#00695C", "#2E7D32", "#558B2F", "#827717",
  "#F57F17", "#EF6C00", "#D84315", "#4E342E", "#37474F",
];

// How much white to mix into a base colour to build progressively lighter tints.
const LIGHT_SHADE_TINTS = [0.15, 0.3, 0.45, 0.6, 0.75, 0.85];

export const getLightShades = (baseColor: string) => {
  const { r, g, b } = hexToRgb(baseColor);
  return LIGHT_SHADE_TINTS.map((tint) => rgbToHex(r + (255 - r) * tint, g + (255 - g) * tint, b + (255 - b) * tint));
};

// The selectable circle options for a child/sub-status colour picker — light
// tints of the parent colour, excluding any tint that equals the parent exactly.
export const getChildColorOptions = (parentColor: string) =>
  getLightShades(parentColor).filter((shade) => shade.toUpperCase() !== parentColor.toUpperCase());

// Suggests a child/sub-status colour: a light tint of its parent, cycling by
// position so siblings differ, guaranteed not to equal the parent exactly.
export const pickChildColor = (parentColor: string, position: number) => {
  const shades = getLightShades(parentColor);
  const safePosition = position >= 0 ? position : 0;
  let color = shades[safePosition % shades.length];
  if (color.toUpperCase() === parentColor.toUpperCase()) {
    color = shades[(safePosition + 1) % shades.length];
  }
  return color;
};

const relativeLuminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const [lr, lg, lb] = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
};

const contrastRatio = (a: string, b: string) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: h * 60, s, l };
};

const hslToHex = (h: number, s: number, l: number) => {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
};

// Darkens `color` just enough to be readable on `background`, staying in the
// same colour family: only lightness drops (saturation is kept), and yellow
// hues drift toward amber/orange since dark yellow otherwise turns brown/olive.
const darkenUntilReadable = (color: string, background: string, minRatio = 3) => {
  const { r, g, b } = hexToRgb(color);
  let { h, s, l } = rgbToHsl(r, g, b);
  let result = rgbToHex(r, g, b);
  for (let i = 0; i < 40 && contrastRatio(result, background) < minRatio; i++) {
    l = Math.max(0, l - 0.02);
    if (h > 34 && h < 75) h = Math.max(34, h - 1);
    result = hslToHex(h, s, l);
  }
  return result;
};

const isValidHex = (hex?: string | null): hex is string =>
  !!hex && /^#?[0-9a-fA-F]{6}$/.test(hex.trim());

const PALE_CHIP_BG_LIGHTNESS = 0.9;

export interface IStatusChipColors {
  text: string;
  bg: string;
  border: string;
}

// Chip colours for a (sub-)status. The master shade (or one close to it) keeps
// the outlined look (label-coloured text on a faint tint). A light tint can't
// be read as text, so it becomes the solid background and the text uses the
// master colour, darkened if it would still blend into that background.
export const getStatusChipColors = (
  labelColour?: string | null,
  masterColour?: string | null,
): IStatusChipColors | null => {
  const label = isValidHex(labelColour) ? labelColour : isValidHex(masterColour) ? masterColour : null;
  if (!label) return null;
  const master = isValidHex(masterColour) ? masterColour : label;

  // "Light" means a clearly lighter tint of its master (sub-status colours are
  // white-mixed tints of the master), not light in absolute terms — otherwise
  // a naturally light master like yellow would never get the outlined look.
  const labelRgb = hexToRgb(label);
  const masterRgb = hexToRgb(master);
  const labelLightness = rgbToHsl(labelRgb.r, labelRgb.g, labelRgb.b).l;
  const masterLightness = rgbToHsl(masterRgb.r, masterRgb.g, masterRgb.b).l;
  if (labelLightness - masterLightness < 0.12) {
    return { text: label, bg: `${label}20`, border: label };
  }

  // Mid-tone tints (e.g. #f8a55d) are too dark to sit behind same-family text,
  // so the background is lifted to a pale version of the tint, which keeps the
  // master-coloured text close to its original shade.
  const labelHsl = rgbToHsl(labelRgb.r, labelRgb.g, labelRgb.b);
  const bg = hslToHex(labelHsl.h, labelHsl.s, Math.max(labelHsl.l, PALE_CHIP_BG_LIGHTNESS));
  const text = darkenUntilReadable(master, bg);
  return { text, bg, border: text };
};
